// Workspace v2 auth API client (HttpOnly cookie pivot)
// D3 SSO contract per 00_Workspace_Architecture_Research/06_d3_sso_aad_config_2026-05-28.md
// Cookie-based session — JS never touches the token; browser auto-sends cookie on every fetch.
// XSS-safe vs v1's localStorage gap (v1 audit 04_v1_Auth_Frontend_Audit.md:123).

const API_BASE = "/api/workspace/auth";
const AUTH_REQUEST_TIMEOUT_MS = 8_000;

const FETCH_OPTS: RequestInit = {
  credentials: "include", // send + accept the HttpOnly cookie
};

export interface LoginUrlResponse {
  authorize_url: string;
  state: string;
}

export interface UserPayload {
  sub: string;
  email?: string | null;
  preferred_username?: string | null;
  name?: string | null;
  session_expires_at?: number | null;
  server_time?: number | null;
  /** Client-only timing marker used to derive a conservative local deadline. */
  session_request_started_at_ms?: number;
}

export interface CallbackResponse {
  user: UserPayload;
}

async function readErrorDetail(res: Response): Promise<string> {
  try {
    const data = (await res.json()) as { detail?: unknown };
    if (typeof data.detail === "string") return data.detail;
    if (
      typeof data.detail === "object"
      && data.detail !== null
      && "message" in data.detail
      && typeof data.detail.message === "string"
    ) return data.detail.message;
    return "";
  } catch {
    return "";
  }
}

export class AuthApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "AuthApiError";
  }
}

function withRequestTiming(
  user: UserPayload,
  requestStartedAtMs: number,
): UserPayload {
  return { ...user, session_request_started_at_ms: requestStartedAtMs };
}

export async function fetchLoginUrl(): Promise<LoginUrlResponse> {
  const res = await fetch(`${API_BASE}/login-url`, FETCH_OPTS);
  if (!res.ok) throw new Error(`login-url failed: HTTP ${res.status} ${await readErrorDetail(res)}`);
  return (await res.json()) as LoginUrlResponse;
}

export async function exchangeCode(
  code: string,
  redirectUri: string,
  state: string,
): Promise<CallbackResponse> {
  const requestStartedAtMs = Date.now();
  const res = await fetch(`${API_BASE}/callback`, {
    ...FETCH_OPTS,
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code, redirect_uri: redirectUri, state }),
  });
  if (!res.ok) throw new Error(`callback failed: HTTP ${res.status} ${await readErrorDetail(res)}`);
  const payload = (await res.json()) as CallbackResponse;
  return { ...payload, user: withRequestTiming(payload.user, requestStartedAtMs) };
}

export async function fetchMe(): Promise<UserPayload | null> {
  const requestStartedAtMs = Date.now();
  const res = await fetch(`${API_BASE}/me`, {
    ...FETCH_OPTS,
    signal: AbortSignal.timeout(AUTH_REQUEST_TIMEOUT_MS),
  });
  if (res.status === 401) return null; // unauthenticated · expected
  if (!res.ok) throw new Error(`me failed: HTTP ${res.status} ${await readErrorDetail(res)}`);
  return withRequestTiming((await res.json()) as UserPayload, requestStartedAtMs);
}

export async function refreshSession(): Promise<UserPayload> {
  const requestStartedAtMs = Date.now();
  const res = await fetch(`${API_BASE}/refresh`, {
    ...FETCH_OPTS,
    method: "POST",
    signal: AbortSignal.timeout(AUTH_REQUEST_TIMEOUT_MS),
  });
  if (!res.ok) {
    throw new AuthApiError(
      `session refresh failed: HTTP ${res.status} ${await readErrorDetail(res)}`.trim(),
      res.status,
    );
  }
  const payload = (await res.json()) as CallbackResponse;
  return withRequestTiming(payload.user, requestStartedAtMs);
}

// 6.5 de-mock: the shell's identity source (identity + server-derived roles + tenant). Replaces the
// auth-bridge DEFAULT_USER. FAIL-CLOSED by contract: ANY non-200 (401 unauth, 500 DB-down, network)
// resolves to null — the caller renders signed-out, NEVER a default/stale user (R-L1).
export interface SessionIdentity {
  sub: string;
  email?: string | null;
  name?: string | null;
  tenant?: string | null;
  roles: string[];
  user_id?: string | null;
  access_status?: "PENDING" | "ACTIVE" | "DISABLED" | "INACTIVE";
  access_request_status?: "PENDING" | "APPROVED" | "DENIED" | "CANCELLED" | null;
  app_access?: string[];
  tab_access?: string[];
}

export async function fetchSession(): Promise<SessionIdentity | null> {
  try {
    // 8s timeout so a hung /session (DB deadlock / slow-loris) can't wedge hydration forever
    // (post-build red-team MED). Timeout aborts → catch → null → signed-out, never a blank shell.
    const res = await fetch(`${API_BASE}/session`, { ...FETCH_OPTS, signal: AbortSignal.timeout(8000) });
    if (!res.ok) return null; // 401/500/anything → signed-out (fail-closed)
    return (await res.json()) as SessionIdentity;
  } catch {
    return null; // network error OR timeout → signed-out
  }
}

export async function requestAccess(message?: string): Promise<{ id: string; status: string }> {
  const res = await fetch(`${API_BASE}/access-request`, {
    ...FETCH_OPTS,
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ message: message?.trim() || null }),
  });
  if (!res.ok) {
    throw new Error(`access request failed: HTTP ${res.status} ${await readErrorDetail(res)}`);
  }
  return (await res.json()) as { id: string; status: string };
}

export async function logoutServer(): Promise<void> {
  await fetch(`${API_BASE}/logout`, {
    ...FETCH_OPTS,
    method: "POST",
    keepalive: true,
    signal: AbortSignal.timeout(3000),
  });
}

// DEV-ONLY · OS13 workaround · returns 404 unless backend WORKSPACE_DEV_MOCK_AUTH=true
export async function devMockLogin(): Promise<CallbackResponse> {
  const requestStartedAtMs = Date.now();
  const res = await fetch(`${API_BASE}/dev-mock-login`, {
    ...FETCH_OPTS,
    method: "POST",
  });
  if (!res.ok) throw new Error(`dev-mock-login failed: HTTP ${res.status} ${await readErrorDetail(res)}`);
  const payload = (await res.json()) as CallbackResponse;
  return { ...payload, user: withRequestTiming(payload.user, requestStartedAtMs) };
}

// DEV-ONLY role picker (spec 48.24 roles slice) · returns 404 unless backend
// WORKSPACE_DEV_MOCK_AUTH=true. Grants the session actor a GLOBAL role so the PDP + confidential-wall
// ADMIN_TIER gates behave as that role — for testing member/non-member/admin behaviour.
const DEV_BASE = "/api/workspace/dev";

// Mirrors the backend ASSIGNABLE_ROLES (pdp.py) + the non-member choice.
export const DEV_ROLES = [
  "Admin",
  "SuperAdmin",
  "Sanitizer",
  "ClientUser",
  "Member",
  "none",
] as const;
export type DevRole = (typeof DEV_ROLES)[number];

export interface ActAsResponse {
  actor: string;
  role: string;
  effective_roles: string[];
  available_roles: string[];
}

export async function actAs(role: DevRole): Promise<ActAsResponse> {
  const res = await fetch(`${DEV_BASE}/act-as`, {
    ...FETCH_OPTS,
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ role }),
  });
  if (!res.ok) throw new Error(`act-as failed: HTTP ${res.status} ${await readErrorDetail(res)}`);
  return (await res.json()) as ActAsResponse;
}
